import { GridApi, GridReadyEvent } from 'ag-grid-community';
import React, { FC, useEffect, useState } from 'react';
import { Column, SelectionsProps } from 'components/atoms/grid';
import { TaskGrid } from 'components/organisms/task-grid';
import { useTranslation } from 'hooks/use-translation';
import { TaskField } from 'stores/fields';
import { GridChangeEvent } from '../grid-custom-column';
import { FilterFunction } from './filter-functions';
import { useDeleteTasks } from './use-delete-tasks';
import { useUpdateServerTasks } from './use-my-task-update-tasks ';
import { useUpdateTasks } from './use-update-tasks';

interface Props extends Omit<SelectionsProps, 'multiSelect' | 'isSelectable'> {
  filterFunction: FilterFunction | null;
  progress: number;
  taskFields: Record<number, TaskField>;
  fields_loading: boolean;
  isSelectedAll: boolean;
  isPaginationEnabled: boolean;
  columns: Column[];
  gridApi?: GridApi;
  onGridReady: (event: GridReadyEvent) => void;
  isInitialLoading: boolean;
  handleGridChange: (data: GridChangeEvent) => void;
}

export const AllTasks: FC<Props> = ({
  filterFunction,
  progress,
  taskFields,
  selected,
  onSelectionChange,
  selectableRef,
  fields_loading,
  isSelectedAll,
  isPaginationEnabled,
  columns,
  gridApi,
  onGridReady,
  isInitialLoading,
  handleGridChange,
}) => {
  const { updateMultipleTasks, updateSingleTask } = useUpdateTasks({
    isAllTask: isSelectedAll,
  });
  const {
    updateMultipleTasks: mutateBulkTasks,
    updateSingleTask: mutateSingleTask,
  } = useUpdateServerTasks();
  const no_matching_data = useTranslation('all_tasks.no_matching_data');
  const no_data = useTranslation('all_tasks.no_data');
  const loading_data = useTranslation('all_tasks.loading_data');

  const deleteTasks = useDeleteTasks(isPaginationEnabled, gridApi);
  const loading = fields_loading || isInitialLoading;

  const [numberOfRows, setNumberOfRows] = useState(
    loading ? Number.MAX_VALUE : 0,
  );

  useEffect(() => {
    const updateNumberOfRows = (e) => {
      setNumberOfRows(e.api.getDisplayedRowCount());
    };

    gridApi?.addEventListener('modelUpdated', updateNumberOfRows);

    return () =>
      gridApi?.removeEventListener('modelUpdated', updateNumberOfRows);
  }, [gridApi]);

  if (fields_loading) {
    return null;
  }

  return (
    <TaskGrid
      columns={columns}
      fields={taskFields}
      filterFunction={filterFunction}
      loading={loading}
      updateMultipleTasks={
        isPaginationEnabled ? mutateBulkTasks : updateMultipleTasks
      }
      updateSingleTask={
        isPaginationEnabled ? mutateSingleTask : updateSingleTask
      }
      onDelete={deleteTasks}
      noMatches={loading || progress < 100 ? loading_data : no_matching_data}
      noData={!loading && numberOfRows ? null : no_data}
      selected={selected}
      onSelectionChange={onSelectionChange}
      selectableRef={selectableRef}
      onReady={onGridReady}
      isPaginationEnabled={isPaginationEnabled}
      gridApi={isPaginationEnabled ? gridApi : undefined}
      handleGridChange={handleGridChange}
    />
  );
};
